/**
 * Build a ReviewObject view from a component + sheet context.
 */

export function deriveLifecycleState(component, { openIssueIds = [] } = {}) {
  if (!component) return 'draft';
  if (openIssueIds.includes(component.id)) return 'flagged';
  const status = component.mappingStatus;
  if (status === 'pending_user_confirm') return 'draft';
  if (status === 'user_confirmed') return 'verified';
  if (status === 'auto_validated') return 'confirmed';
  return 'draft';
}

export function buildReviewObject({
  component,
  sheetId,
  sheetIrKey,
  linkedIssueIds = [],
}) {
  if (!component) return null;
  return {
    componentId: component.id,
    sheetId: sheetId || component.sheetId,
    sheetIrKey,
    pageIndex: component.pageIndex ?? 0,
    component,
    lifecycleState: deriveLifecycleState(component, { openIssueIds: linkedIssueIds }),
    linkedIssueIds,
  };
}

export function sheetDisplayName(sheet) {
  const key = sheet?.sourcePdfKey || sheet?.ir?.storage?.sourceKey || '';
  const base = key.split('/').pop() || sheet?.sheetId || 'Sheet';
  return base.replace(/\.pdf$/i, '');
}

export function sheetIndexingLabel(sheet) {
  const status = sheet?.extractionStatus || sheet?.ir?.extractionStatus;
  if (status === 'extracting') {
    const done = sheet?.pagesExtracted ?? sheet?.ir?.pagesExtracted ?? 0;
    const total = sheet?.pageCount ?? sheet?.ir?.pageCount ?? 0;
    if (total > 0) return `Indexing ${done}/${total}`;
    return 'Indexing…';
  }
  const unknowns = sheet?.unknownsRemaining ?? sheet?.ir?.mappingCheck?.unknownsRemaining ?? 0;
  if (unknowns > 0) return `${unknowns} unknowns`;
  return 'Ready';
}

export function sheetIndexingTone(sheet) {
  const status = sheet?.extractionStatus || sheet?.ir?.extractionStatus;
  if (status === 'extracting') return 'progress';
  const unknowns = sheet?.unknownsRemaining ?? 0;
  if (unknowns > 0) return 'warn';
  return 'ready';
}

/** Resolve component that owns a vector/text entity id. */
export function findComponentForEntity(components, entityId) {
  if (!entityId || !components?.length) return null;
  return components.find((c) => (c.entityIds || []).includes(entityId)) || null;
}

/** Cross-refs attached to one component. */
export function crossRefsForComponent(sheetIr, componentId) {
  if (!componentId || !sheetIr?.crossRefs) return [];
  return sheetIr.crossRefs.filter((r) => r.componentId === componentId);
}

/** Find components whose label/value fuzzy-matches issue evidence text. */
export function findComponentsMatchingText(components, text, pageIndex = null) {
  const q = String(text || '').trim().toLowerCase();
  if (!q || q.length < 4) return [];
  return (components || []).filter((c) => {
    if (pageIndex != null && c.pageIndex != null && c.pageIndex !== pageIndex) return false;
    const label = `${c.label || ''} ${c.value || ''}`.toLowerCase();
    return label.includes(q) || q.includes(label.trim());
  });
}

/** Resolve a detail callout target on a destination sheet IR. */
export function findComponentForCrossRefTarget(ir, { targetDetail, targetSheet }) {
  if (!ir?.components?.length) return null;
  const detail = targetDetail != null ? String(targetDetail) : '';
  const sheet = (targetSheet || '').toUpperCase();

  return ir.components.find((c) => {
    const text = (c.label || c.value || '').trim();
    if (!text) return false;
    if (detail && sheet && text.replace(/\s/g, '').includes(`${detail}/${sheet}`.replace(/\s/g, ''))) {
      return true;
    }
    if (detail && text === detail) return true;
    if (sheet && text.toUpperCase().includes(sheet) && (!detail || text.includes(detail))) {
      return true;
    }
    return false;
  }) || null;
}
